/**
 * **The auto-activation acknowledgement** — the transient, non-blocking "your mech just took its
 * turn" signal (`projects/pre-activation-affordances.md` Phase 4, item 4.1).
 *
 * ## Why this exists, and why deleting it would be a mistake
 *
 * This project removed the ACTIVATE TURN gate: a `"ready"` mech's first real action now folds its
 * own `activate_unit`, at the room, for every channel (D1/D5). That is the whole point — the
 * developer kept forgetting the gate — and it introduces exactly one new failure mode, recorded as
 * the tracker's first Risk and repeated here because a future reader meets the code before the
 * tracker:
 *
 * > Once activation is implicit, **clicking an ability on the wrong mech silently commits that
 * > mech's turn.** Every observer sees the side alternate; undo reaches it (the activation is open,
 * > so `callerHasOpenActivation` is true) but **only if the player notices**.
 *
 * The acknowledgement is what makes "notices" likely. It is not decoration; it is the one
 * affordance that turns a silent commitment into a visible one. If you are about to remove it
 * because it looks like a flourish, that is the paragraph you are removing it against.
 *
 * ## The settled behaviour (developer ruling, 2026-08-27 — not a fork to re-open)
 *
 * - **Transient** — it goes away on its own.
 * - **Non-blocking** — it never gates the action that triggered it.
 * - **No dismissal** — nothing to click. (Contrast {@link RejectionToast}, which keeps its ✕.)
 * - **Fires only on an AUTO-activation** — never on an explicit `activate_unit` from the ACTIVATE
 *   TURN button or the turn-tracker HUD. The acknowledgement exists to tell a player about a state
 *   change they did *not* ask for; firing it on the one they did ask for is noise.
 *
 * He **declined a first-action confirm** for the reason it was offered against — it reintroduces
 * the click he asked us to remove, relabelled. The *visual treatment* is the only open question and
 * it is routed to his eye as an owed task; the behaviour above is settled.
 *
 * ## How "auto, not explicit" is decided — and why it is decided this way round
 *
 * Nothing on the wire distinguishes the two: a folded `activate_unit` and an explicitly pressed one
 * are the same command. Only the **call site** knows. So the three auto-activating call sites arm
 * this signal and the explicit ones do not:
 *
 * | call site | channel | arms? |
 * |---|---|---|
 * | {@link RemoteEncounterController.sendIntent} | every D5 intent (fire, Barrage, tech, the bespoke verbs, `request_move`'s siblings) | **yes**, when {@link wouldFoldActivation} |
 * | {@link RemoteEncounterController.dispatchMove} | the token drag | **yes**, when the dry-run previewed |
 * | {@link RemoteEncounterController.dispatchGesture} | the D6 command pair (`boost`) | **yes**, when the pair leads with `activate_unit` |
 * | `dispatch({type: "activate_unit"})` — the inspector header's START TURN and PASS (`TurnControls`) | explicit | **no** |
 *
 * **Tagging the auto sites, rather than suppressing the explicit ones, is deliberate.** The two are
 * not symmetric in how they fail: a future auto path that forgets to arm is silently *quiet* (a
 * missed nicety), while a future explicit path that forgets to suppress would *flash on a press the
 * player just made* — precisely the noise the ruling rules out. Fail toward silence.
 *
 * ## Arm, then CONFIRM — the reason this is not simply "fire at the gesture"
 *
 * D3: *a rejected or no-op action must not commit the activation.* An out-of-range or LoS-blocked
 * attack rejects without burning the unit's turn, so acknowledging at the gesture would announce a
 * turn that never opened. The arm is therefore only a *candidate*: it is resolved against the
 * server's own authoritative state on the next `encounter_change`, by {@link armedFoldOutcome}.
 * That also means **over-arming is harmless and self-correcting** — a reaction intent, a held-action
 * release, or a GM wave-through arms and then simply never fires, because the room did not fold.
 *
 * It is also what makes the acknowledgement **local to the acting client** by construction, which
 * is the claim `frontend/e2e/pre-activation-two-client.e2e.ts` asserts: only the client that made
 * the gesture ever arms. A bystanding player's feedback is the turn tracker flipping, and nothing
 * else — the server broadcasts one board, not one banner.
 */
import { perUnitState, type EncounterState } from "@lancer-vtt/domain";

/**
 * A landed auto-activation, ready to render. Ephemeral — like {@link PingEvent} and
 * {@link EncounterRejection} it is never part of the reconciled encounter state, and it exists only
 * on the client whose own gesture caused it.
 *
 * `seq` is monotonic so a *repeat* auto-activation of the same unit (an ELITE/ULTRA's second
 * activation, or the same mech again next round) mints a fresh object and re-shows the card.
 */
export type AutoActivationNotice = {
  /** The unit whose turn the fold opened. The renderer names it — see the Risk in this module's header. */
  combatantId: string;
  /** Monotonic counter — a fresh acknowledgement always has a higher seq (drives the re-show). */
  seq: number;
};

/**
 * What to do with an armed candidate when an authoritative `encounter_change` lands.
 *  - `fire` — the fold committed: this unit is now the acting actor. Raise the acknowledgement.
 *  - `hold` — the unit is still `"ready"`: our resolution has not landed yet and this change was
 *    somebody else's (another player moving, a GM edit, a status tick). Keep waiting.
 *  - `drop` — the unit is anything else (`activated`, `on_deck`, destroyed, gone, never an actor):
 *    whatever we armed for is not going to happen. Forget it.
 */
export type ArmedFoldOutcome = "fire" | "hold" | "drop";

/**
 * Resolve an armed candidate against the **server's** state — the one function the acknowledgement
 * turns on, exported and unit-tested directly because this repo has no jsdom/headless React
 * renderer (see `combatWindows/inspectorEconomy.test.ts`'s header for the convention).
 *
 * Read off {@link perUnitState}, the same display selector the fold's own `"ready"` arm keys on
 * (`backend/src/encounterActivationFold.ts`), rather than a second hand-rolled `turn.phase ===
 * "acting" && turn.actor === id` — so the acknowledgement and the disposition cannot drift on what
 * "this unit's turn is open" means.
 *
 * **`hold` is load-bearing, not caution.** Between a gesture and its resolution broadcast, any
 * other client's change can land first; consuming the arm on the first change to arrive would drop
 * the acknowledgement for the very shot that earned it. Holding while the unit is still `"ready"`
 * is what keeps it — and dropping the moment it is anything else is what stops a stale arm from
 * firing on an unrelated change minutes later.
 */
export function armedFoldOutcome(state: EncounterState, armedCombatantId: string): ArmedFoldOutcome {
  const unitState = perUnitState(state)[armedCombatantId];
  if (unitState === "active") return "fire";
  return unitState === "ready" ? "hold" : "drop";
}
